import React, { useCallback, useContext } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import ModalContext from 'contexts/ModalContext'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'LuxKit/Button/TextButton'
import type { CruiseOfferView } from 'hooks/Cruise/useCruiseOfferView'

interface Props {
  view: CruiseOfferView
  isStandalone?: boolean
  cabinCategoryFromFilter?: string
}

function CruiseTilePrices(props: Props) {
  const intl = useIntl()
  const {
    view,
    isStandalone,
    cabinCategoryFromFilter,
  } = props
  const {
    cabinCategory,
    lowestPrice,
    hasOnlySuitesAvailable,
    discountPills,
  } = view
  const showModal = useContext(ModalContext)
  const openDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  return (
    <Group gap={8} direction="vertical" fullWidth noDisplayWhenEmpty>
      <Group
        fullWidth
        gap={4}
        direction="horizontal"
        tabletDirection="vertical"
        horizontalAlign="space-between"
        tabletHorizontalAlign="start"
        verticalAlign="end"
        noDisplayWhenEmpty
      >
        {(isStandalone || cabinCategoryFromFilter || hasOnlySuitesAvailable) && <>
          <div>
            {cabinCategory && (
              <PriceRowPriceCaption>
                {intl.formatMessage(
                  {
                    id: 'nvNaUL613x',
                    defaultMessage: '{cabinCategory} from',
                    description: 'Caption showing cabin category name followed by starting price indicator',
                  },
                  { cabinCategory },
                )}
              </PriceRowPriceCaption>
            )}
            {lowestPrice && (
              <PriceRowPrice
                size="L"
                price={lowestPrice.primaryPrice.total}
                saleUnit={lowestPrice.primaryPrice.type}
              />
            )}
            {lowestPrice?.secondaryPrice && <PriceRowCaption>
              <FormattedMessage
                id="asuxn0hT13"
                defaultMessage="{price} /{priceType} · {twinShareLabel}"
                description="Cruise secondary price with sale unit and twin share label"
                values={{
                  price: <FormatCurrency value={lowestPrice.secondaryPrice.total} />,
                  priceType: lowestPrice.secondaryPrice.type,
                  twinShareLabel: intl.formatMessage({
                    id: 'wG9RjdIdO6',
                    defaultMessage: 'Twin Share',
                    description: 'Label indicating twin share pricing for cruise cabin with secondary price',
                  }),
                }}
              />
            </PriceRowCaption>}
            {!lowestPrice?.secondaryPrice && discountPills?.price && discountPills.discountPercentage && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={discountPills.price}
              discountPercentage={discountPills.discountPercentage}
              onInfoIconClick={openDiscountTermsAndConditionsModal}
            />}
            {!lowestPrice?.secondaryPrice && <PriceRowCaption>{intl.formatMessage({
              id: 'rzR7jiFXMH',
              defaultMessage: 'Twin Share',
              description: 'Label indicating twin share pricing for cruise cabin',
            })}
            </PriceRowCaption>}
            <PriceRowTaxesAndFees />
          </div>
          <CSSBreakpoint max="mobile">
            <TextButton kind="primary">
              {intl.formatMessage({
                id: 'O5vhRtqGqE',
                defaultMessage: 'View',
                description: 'Button text to view cruise offer details on mobile',
              })}
            </TextButton>
          </CSSBreakpoint>
        </>}
      </Group>
    </Group>
  )
}

export default CruiseTilePrices
